import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CalendarDays, Search, X } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import type { DateRange } from 'react-day-picker'

interface FilterBarProps {
  dateFrom: string | null
  dateTo: string | null
  onDateRangeChange: (from: string | null, to: string | null) => void
  search: string
  onSearchChange: (s: string) => void
}

export function FilterBar({
  dateFrom,
  dateTo,
  onDateRangeChange,
  search,
  onSearchChange,
}: FilterBarProps) {
  const calendarRange: DateRange | undefined =
    dateFrom
      ? { from: parseISO(dateFrom), to: dateTo ? parseISO(dateTo) : undefined }
      : undefined

  const handleRangeSelect = (range: DateRange | undefined) => {
    onDateRangeChange(
      range?.from ? format(range.from, 'yyyy-MM-dd') : null,
      range?.to ? format(range.to, 'yyyy-MM-dd') : null,
    )
  }

  const hasDateFilter = !!dateFrom || !!dateTo

  return (
    <div className="flex items-center justify-start gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 justify-start text-left font-normal">
            <CalendarDays className="mr-1.5 h-3.5 w-3.5 shrink-0" />
            {dateFrom || dateTo ? (
              <span className="truncate text-xs">
                {dateFrom ? format(parseISO(dateFrom), 'd MMM yyyy') : '...'}{' – '}
                {dateTo ? format(parseISO(dateTo), 'd MMM yyyy') : '...'}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">Date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={calendarRange}
            onSelect={handleRangeSelect}
            numberOfMonths={2}
            classNames={{
              day_selected: 'bg-blue-600 text-white hover:bg-blue-600 hover:text-white focus:bg-blue-600 focus:text-white',
              day_range_middle: 'aria-selected:bg-blue-100 aria-selected:text-blue-900',
              day_range_end: 'day-range-end bg-blue-600 text-white',
            }}
          />
        </PopoverContent>
      </Popover>
      {hasDateFilter && (
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={() => onDateRangeChange(null, null)}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}

      <div className="relative w-full max-w-md">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
        <Input
          placeholder="Search counterparty / description..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-9 w-full bg-white pl-8 text-xs"
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}
